import {ChangeDetectionStrategy, Component, OnInit, ViewChild} from '@angular/core';
import {formatDate} from '@angular/common';
import {ActivatedRoute} from '@angular/router';
import {combineLatest, map} from 'rxjs';
import {Dropdown} from 'primeng/dropdown';
import {TranslateService} from '@ngx-translate/core';
import {PyramidCategory} from 'src/app/components/categories-pyramid-display/categories-pyramid-display.component';
import {TeamDynamicTeamFacade} from '../../facade/team-dynamic-team.facade';
import {TeamDynamicRequestDto} from '../../dto/team-dynamic-request.dto';
import {StatementVm} from '../../models/team-dynamic.vm';
import {TeamIndicatorRowVm} from '../../models/team-dynamic-team.vm';
import {StatementFilters} from '../../components/statements/statements.component';
import {TeamIndicatorsComponent} from '../../components/team-indicators/team-indicators.component';

@Component({
  selector: 'app-team-dynamic-team-page',
  templateUrl: './team-dynamic-team-page.component.html',
  styleUrls: ['./team-dynamic-team-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TeamDynamicTeamPageComponent implements OnInit {
  private static readonly VISIBLE_AVATARS = 4;

  @ViewChild('periodDropdown') periodDropdown: Dropdown | undefined;
  @ViewChild(TeamIndicatorsComponent) indicators: TeamIndicatorsComponent | undefined;

  readonly vm$ = combineLatest([
    this.teamFacade.loading$,
    this.teamFacade.header$,
    this.teamFacade.healthScore$,
    this.teamFacade.pyramidCategories$,
    this.teamFacade.pyramidLegend$,
    this.teamFacade.indicatorRows$,
    this.teamFacade.insights$,
    this.teamFacade.teamScores$,
    this.teamFacade.workspaceScores$,
    this.teamFacade.loadingScores$,
    this.teamFacade.scoreChips$,
    this.teamFacade.openIndicatorUuid$,
    this.teamFacade.indicatorScores$,
    this.teamFacade.loadingIndicator$,
    this.teamFacade.indicatorStatements$,
    this.teamFacade.loadingStatements$,
    this.teamFacade.statementResponses$
  ]).pipe(
    map(([loading, header, healthScore, pyramidCategories, pyramidLegend, indicatorRows, insights,
           teamScores, workspaceScores, loadingScores, scoreChips, openIndicatorUuid,
           indicatorScores, loadingIndicator, indicatorStatements, loadingStatements,
           statementResponses]) => ({
      loading,
      header,
      healthScore,
      pyramidCategories,
      pyramidLegend,
      indicatorRows,
      insights,
      teamScores,
      workspaceScores,
      loadingScores,
      scoreChips,
      openIndicatorUuid,
      indicatorScores,
      loadingIndicator,
      indicatorStatements,
      loadingStatements,
      statementResponses,
      visibleMembers: (header?.members ?? []).slice(0, TeamDynamicTeamPageComponent.VISIBLE_AVATARS),
      hiddenMembers: Math.max(0, (header?.members?.length ?? 0) - TeamDynamicTeamPageComponent.VISIBLE_AVATARS)
    }))
  );

  public teamUuid: string = '';
  public period: 'all' | 'custom' = 'all';
  public periodLabel: string = '';
  public periodOptions: { label: string, value: string }[] = [];
  public periodRangeDates: Date[] | null = null;
  public showPeriodCalendar: boolean = false;
  public activeChipUuid: string | null = null;

  private requestPayload: TeamDynamicRequestDto = {
    startDate: undefined,
    endDate: undefined,
    timedQuestionCollectionUuid: null
  };

  constructor(private teamFacade: TeamDynamicTeamFacade, private translate: TranslateService,
              private activeRoute: ActivatedRoute) {
  }

  ngOnInit(): void {
    this.setPeriodOptions();
    this.setPeriodLabel();
    this.teamUuid = this.activeRoute.snapshot.paramMap.get('teamUuid') ?? '';
    if (this.teamUuid) {
      this.teamFacade.loadData(this.teamUuid, this.requestPayload);
    }
  }

  onPeriodChange(event: any) {
    if (event.value === 'all') {
      this.selectAllTime();
    }
  }

  selectAllTime() {
    this.period = 'all';
    this.periodRangeDates = null;
    this.showPeriodCalendar = false;
    this.setPeriodLabel();
    this.requestPayload = {...this.requestPayload, startDate: undefined, endDate: undefined};
    this.teamFacade.reloadForPeriod(this.teamUuid, this.requestPayload);
  }

  submitPeriodRange() {
    const range = this.periodRangeDates;
    if (!range || range.length !== 2 || !range[1]) {
      return;
    }
    this.period = 'custom';
    this.setPeriodLabel();
    this.periodDropdown?.hide();
    this.requestPayload = {
      ...this.requestPayload,
      startDate: formatDate(range[0], 'dd.MM.yyyy', 'en-US'),
      endDate: formatDate(range[1], 'dd.MM.yyyy', 'en-US')
    };
    this.teamFacade.reloadForPeriod(this.teamUuid, this.requestPayload);
  }

  clearPeriodRange() {
    this.periodRangeDates = null;
  }

  cancelPeriodRange() {
    this.clearPeriodRange();
    this.periodDropdown?.hide();
  }

  onScoreChip(uuid: string | null) {
    if (this.activeChipUuid === uuid) {
      return;
    }
    this.activeChipUuid = uuid;
    this.requestPayload = {...this.requestPayload, timedQuestionCollectionUuid: uuid};
    this.teamFacade.loadScoreDevelopment(this.teamUuid, this.requestPayload);
  }

  onPyramidCategory(category: PyramidCategory) {
    this.teamFacade.openIndicator(this.teamUuid, category.uuid, this.requestPayload);
    this.indicators?.scrollToIndicator(category.uuid);
  }

  onIndicatorOpened(row: TeamIndicatorRowVm) {
    this.teamFacade.openIndicator(this.teamUuid, row.uuid, this.requestPayload);
  }

  onIndicatorClosed() {
    this.teamFacade.closeIndicator();
  }

  onStatementFilters(filters: StatementFilters) {
    this.requestPayload = {
      ...this.requestPayload,
      statementsSort: filters.sort,
      statementsStartDate: filters.startDate,
      statementsEndDate: filters.endDate
    };
    this.teamFacade.loadIndicatorStatements(this.teamUuid, this.requestPayload);
  }

  onStatementOpened(statement: StatementVm) {
    this.teamFacade.loadStatementResponses(this.teamUuid, statement);
  }

  private setPeriodOptions() {
    this.periodOptions = [
      {label: this.translate.instant('custom_poll_main_reporting_all_time'), value: 'all'},
      {label: this.translate.instant('wo_team_dynamic_custom_period'), value: 'custom'}
    ];
  }

  private setPeriodLabel() {
    const range = this.periodRangeDates;
    this.periodLabel = this.period === 'custom' && range?.length === 2 ?
      formatDate(range[0], 'dd.MM.yyyy', 'en-US') + ' - ' +
      formatDate(range[1], 'dd.MM.yyyy', 'en-US') :
      this.translate.instant('custom_poll_main_reporting_all_time');
  }
}
